import { useDarkMode } from '@/contexts/DarkModeContext';
import Alert from 'antd/es/alert';
import Form from 'antd/es/form';
import Typography from 'antd/es/typography';
import React, { useCallback, useState } from 'react';
import { styled } from 'styled-components';
import { JsonEditor } from 'ui-components';

import type { MapBlockConfig, PipelineBlock } from '@mai/types';

import { EnumSelect, NumberRange } from './shared';

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.marginXS}px;
`;

const EditorLabel = styled(Typography.Text)`
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.marginXXS}px;
`;

const EditorHelp = styled(Typography.Text)`
  color: ${({ theme }) => theme.colorTextSecondary};
  font-size: ${({ theme }) => theme.fontSizeSM}px;
  margin-bottom: ${({ theme }) => theme.marginXS}px;
`;

interface MapBlockEditorProps {
  block: PipelineBlock & { type: 'map' };
  onConfigChange: (config: MapBlockConfig) => void;
}

const supportedBlockTypes = [
  'identity',
  'constant',
  'transform',
  'llm',
  'semantic_filter',
  'string_template',
];

export const MapBlockEditor: React.FC<MapBlockEditorProps> = ({
  block,
  onConfigChange,
}) => {
  const [form] = Form.useForm();
  const [error, setError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [isDarkMode] = useDarkMode();

  // Initialize form with current values
  React.useEffect(() => {
    form.setFieldsValue({
      target_block_type: block.config.target_block_type || '',
      max_concurrency: block.config.max_concurrency || 5,
    });
  }, [block.config, form]);

  // Convert the current target_config to a string for the editor with prettification
  const currentConfigString = React.useMemo(() => {
    if (
      !block.config.target_config ||
      Object.keys(block.config.target_config).length === 0
    ) {
      return '{}';
    }
    try {
      // Prettify the JSON if it's valid
      return JSON.stringify(block.config.target_config, null, 2);
    } catch {
      // If somehow the object is invalid, return empty object
      return '{}';
    }
  }, [block.config.target_config]);

  const handleFormChange = (_: unknown, allValues: Partial<MapBlockConfig>) => {
    onConfigChange({
      ...block.config,
      target_block_type: allValues.target_block_type || '',
      max_concurrency: allValues.max_concurrency || 5,
    });
  };

  const handleJsonChange = useCallback(
    (value: string) => {
      // Only update config when JSON is valid
      if (isValid) {
        try {
          const parsedConfig = JSON.parse(value);
          onConfigChange({
            ...block.config,
            target_config: parsedConfig,
          });
        } catch {
          // Invalid JSON - don't update config
        }
      }
    },
    [block.config, onConfigChange, isValid],
  );

  const handleValidation = useCallback(
    (valid: boolean, errorMessage?: string) => {
      setIsValid(valid);
      setError(errorMessage || null);
    },
    [],
  );

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        size="small"
        onValuesChange={handleFormChange}
      >
        <EnumSelect
          label="Target Block Type"
          name="target_block_type"
          options={supportedBlockTypes.map((type) => ({
            value: type,
            label: type,
          }))}
          help="Type of block to apply to each array item"
          required
        />

        <NumberRange
          label="Max Concurrency"
          name="max_concurrency"
          min={1}
          max={20}
          help="Maximum number of concurrent operations"
        />
      </Form>

      <EditorContainer>
        <EditorLabel>Target Config</EditorLabel>
        <EditorHelp>Configuration object for the target block type</EditorHelp>

        {error && (
          <Alert
            type="error"
            message="Invalid JSON"
            description={error}
            showIcon
          />
        )}

        <JsonEditor
          value={currentConfigString}
          onChange={handleJsonChange}
          onValidate={handleValidation}
          height="200px"
          placeholder="{}"
          theme={isDarkMode ? 'dark' : 'light'}
        />
      </EditorContainer>
    </>
  );
};
